import { ReactElement, Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import { useAuthState } from 'shared/auth/AuthState';
import { getAuthRole, saveCarrierGuid } from 'shared/auth/AuthToken';
import { useSignUpEvent } from 'shared/auth/SignupPageUtils';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { useShowTrialExpiredDialog } from 'shared/errors/data/useShowTrialExpiredDialog';
import { PaywallDialog } from 'shared/errors/PaywallDialog';
import { identifyAnalytics } from 'shared/helpers/Analytics';
import { identifyUser } from 'shared/helpers/Logger';
import { hasSuperDispatchExtensionInstalled } from 'shared/helpers/ExtensionHelper';
import { useViewportMeta } from 'shared/helpers/ResponsiveHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { NavigationHeaderBanner } from 'shared/layout/NavigationHeaderBanner';
import { NavigationHeaderBannerContainer } from 'shared/layout/NavigationHeaderBannerContainer';
import { NotificationStack } from 'shared/layout/notification-stack/NotificationStack';
import { SubscriptionPaymentBanner } from 'shared/layout/notification-stack/core/SubscriptionPaymentBanner';
import { NavigationBarContainer } from 'shared/layout/root/NavigationBarContainer';
import { AppLayoutSidebarLegacy } from 'shared/layout/sidebar/AppLayoutSidebarLegacy';
import { NavbarBottomBar } from 'shared/layout/sidebar/NavbarBottomBar';
import { NavbarContextProvider } from 'shared/layout/sidebar/NavbarContext';
import { useBottomItems } from 'shared/layout/sidebar/useBottomItems';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { ChromeExtensionReminderSnackBar } from 'shared/modules/chrome-extension-reminder/ChromeExtensionReminderSnackBar';
import { useDriverActivationExperiment } from 'shared/modules/experiments/driver-activation/DriverActivationExperiment';
import { useRedirectToSLBExperiment } from 'shared/modules/experiments/redirect-to-slb-experiment/RedirectToSLBExperiment';
import { useLoadboardNotifications } from 'shared/modules/loadboard/LoadboardNotifications';
import { OnboardingDialogs } from 'shared/modules/onboarding/OnboardingDialogs';
import { useOnboardingTourHandler } from 'shared/modules/onboarding/OnboardingIntercomTours';
import { useRatingNotifications } from 'shared/modules/ratings/data/RatingNotifications';
import { useShowAddBillingAddressDialog } from 'shared/modules/subscription/core/useShowAddBillingAddressDialog';
import { SubscriptionAddBillingAddressDialog } from 'shared/modules/subscription/SubscriptionAddBillingAddressDialog';
import {
  useBillingAddress,
  useSubscriptionDetails,
} from 'shared/modules/subscription/SubscriptionAPI';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { TrialExpiredDialog } from '../loadboard/core/TrialExpiredDialog';
import { useDispatcherProfile } from 'shared/modules/dispatcher/DispatcherAPI';
import { useUserPermissions } from 'shared/modules/permission/PermissionAPI';
import { getEnabledFeaturesForIntercom } from 'shared/helpers/Intercom';
import { useLoadsLaunchqueue } from './loads/data/useLoadsLaunchqueue';

const localPaths = {
  claims: '/claims',
  vehicles: '/vehicles',
  dashboard: '/dashboard',
  home: '/home',
  loads: '/loads',
  trips: '/trips',
  tracking: '/tracking',
  drivers: '/drivers',
  contacts: '/contacts',
  users: '/users',
  ratings: '/profile/overview',
  carrierProfile: '/profile',
  reports: '/reports',
  settings: '/settings',
  subscriptionDetails: '/settings/subscription',
  superCard: '/super-card',
  onboardingPackets: '/onboarding-packets',
  logout: '/logout',
} as const;

interface CarrierAppRoutesContainerProps {
  children: ReactElement;
}

function CarrierAppRoutesContainer({
  children,
}: CarrierAppRoutesContainerProps) {
  const authState = useAuthState();
  const { isOpenUpgradeWarningDialog, closeUpgradeWarningDialog } =
    useShowAddBillingAddressDialog();
  const {
    isOpenSubscriptionCancelledDialog,
    closeSubscriptionCancelledDialog,
    trialType,
  } = useShowSubscriptionCancelledDialog();
  const { isOpenTrialExpiredDialog } = useShowTrialExpiredDialog();
  const { data: subscriptionDetails } = useSubscriptionDetails();
  const role = getAuthRole();

  const { bottomItems, hasMenuBadge } = useBottomItems();

  useLoadsLaunchqueue();

  useSignUpEvent();

  if (authState === 'undetermined') {
    return <SplashScreen />;
  }

  return (
    <NavbarContextProvider>
      <NotificationStack />

      <ChromeExtensionReminderSnackBar />
      <OnboardingDialogs />

      {role === 'admin' && (
        <NavigationHeaderBannerContainer>
          <NavigationHeaderBanner />
        </NavigationHeaderBannerContainer>
      )}
      <SubscriptionAddBillingAddressDialog
        open={Boolean(isOpenUpgradeWarningDialog)}
        onClose={closeUpgradeWarningDialog}
      />

      <TrialExpiredDialog
        open={
          Boolean(isOpenSubscriptionCancelledDialog) &&
          !!subscriptionDetails?.customer?.payment_method
        }
        onClose={() => {
          closeSubscriptionCancelledDialog();
          openExternalURL('/loadboard');
        }}
        onReactivate={() => {
          closeSubscriptionCancelledDialog();
          openExternalURL('/tms/settings/subscription');
        }}
        type={trialType === 'expired' ? 'trial_expired' : 'cancelled'}
      />

      {isOpenTrialExpiredDialog &&
        subscriptionDetails?.subscription?.plan?.name === 'lite' &&
        !subscriptionDetails.customer?.payment_method && (
          <PaywallDialog isTrialExpired={isOpenTrialExpiredDialog} />
        )}

      <NavigationBarContainer
        banner={<SubscriptionPaymentBanner />}
        navigationBar={
          <AppLayoutSidebarLegacy
            hasMenuBadge={hasMenuBadge}
            localPaths={localPaths}
          />
        }
        mobileMenuBar={
          <NavbarBottomBar
            items={bottomItems}
            hasMenuBadge={hasMenuBadge}
            localPaths={localPaths}
          />
        }
      >
        {children}
      </NavigationBarContainer>
    </NavbarContextProvider>
  );
}

export function CarrierOutlet() {
  const { data: settings } = useCarrierSettings();
  const hasExtensionInstalled = hasSuperDispatchExtensionInstalled();
  const isWebView = /mobile/i.test(navigator.userAgent);

  useViewportMeta({ width: 960, disabled: isWebView });
  useLoadboardNotifications();
  useRatingNotifications();
  useDriverActivationExperiment();
  useRedirectToSLBExperiment();
  useBillingAddress();
  useDispatcherProfile();
  useUserPermissions();

  useOnboardingTourHandler({
    tourName: 'tour_verification_approval',
    enabled: settings?.carrier.verification_status === 'approved',
  });

  useEffect(() => {
    const user = settings?.user;
    const carrier = settings?.carrier;
    const isSuper = settings?.carrier.verification_status === 'approved';

    if (carrier?.guid) {
      saveCarrierGuid(carrier.guid);
    }

    if (user) {
      identifyUser({
        id: user.guid,
        carrier_guid: settings.carrier.guid,
        carrier_name: settings.carrier.name,
      });
      identifyAnalytics({
        guid: user.guid,
        hash: user.user_hash,
        accountType: settings.company_type,
        experiment_group: settings.groups?.map((e) => e.number),
        sd_importer_installed: hasExtensionInstalled,
        // this list will be converted to string in intercom so make sure that string is not exceeding 255 characters
        enabled_features: getEnabledFeaturesForIntercom(settings.features, [
          'supercard_management',
        ]),
        company: {
          id: carrier?.guid,
          guid: carrier?.guid,
          name: carrier?.name,
          is_super: isSuper,
          superpay_onboarding_status: settings.superpay.verification,
          is_fmcsa_phone_verified: settings.is_fmcsa_phone_verified,
        },
      });
    } else {
      identifyAnalytics();
    }
  }, [settings, hasExtensionInstalled]);
  return (
    <CarrierAppRoutesContainer>
      <Suspense fallback={<ContentProgressIndicator />}>
        <Outlet />
      </Suspense>
    </CarrierAppRoutesContainer>
  );
}
